import React, { useState, useEffect, useRef } from "react";
import ReactGA from "react-ga4";
import { useHiddenTime } from "src/shared/utils";
import "./styles.scss";

function getOnlineStatus() {
  return navigator.onLine ? "online" : "offline";
}

function OnlineStatus() {
  // Lets pick up initial values from `navigtor` only.
  // I thought its OK to not to check for internet cuz of complexity
  const [status, setStatus] = useState(getOnlineStatus);
  const [shouldShowStatus, setShouldShowStatus] = useState(getOnlineStatus() === "offline");
  const [startOfflineMonitor, stopOfflineMonitor] = useHiddenTime();
  const hasMounted = useRef(false);

  const handleInternetStatusChange = (event: MessageEvent) => {
    if (!event.data || event.data.type !== "INTERNET_STATUS") return;

    const status = event.data.value as string;
    setStatus(status);
  };

  const handleOnlineStatusChange = () => {
    const status = getOnlineStatus();
    if (status === "offline") {
      setStatus(status);
      return;
    }

    // If `navigator.onLine` is true, lets get confirmation from sw that user has working internet

    // Oops! No service worker alive for some reason
    // Fallback to `navigator.onLine` status
    if (!navigator.serviceWorker.controller) {
      setStatus(status);
      return;
    }

    navigator.serviceWorker.controller.postMessage({
      type: "CHECK_INTERNET",
    });
  };

  useEffect(() => {
    window.addEventListener("online", handleOnlineStatusChange, false);
    window.addEventListener("offline", handleOnlineStatusChange, false);
    navigator.serviceWorker.addEventListener("message", handleInternetStatusChange, false);

    return () => {
      window.removeEventListener("online", handleOnlineStatusChange);
      window.removeEventListener("offline", handleOnlineStatusChange);
      navigator.serviceWorker.removeEventListener("message", handleInternetStatusChange);
    };
  }, []);

  useEffect(() => {
    if (status === "offline") {
      startOfflineMonitor();
    } else {
      const { visibleTime: offlineTime, startTimestamp, endTimestamp } = stopOfflineMonitor();

      // if startTimestamp === 0, startOfflineMonitor not called
      // offline time < 1 second doesn't makes sense. May be there is glitch in matrix or our code not considering all cases
      if (startTimestamp > 0 && offlineTime > 1000) {
        (ReactGA as any).event({
          category: "Connection Status",
          action: "Offline",
          label: String(startTimestamp),
          value: Math.round(offlineTime), // Should be integer else GA won't accept
          dimension3: Math.round(endTimestamp),
          metric1: Math.round(endTimestamp / 1000),
        });
      }
    }

    // prevents showing online message at first render
    if (!hasMounted.current) {
      hasMounted.current = true;

      // at first render, lets check for internet
      handleOnlineStatusChange();
      return;
    }

    setShouldShowStatus(true);

    if (status === "offline") return;

    const timerId = setTimeout(() => setShouldShowStatus(false), 2500);
    return () => clearTimeout(timerId);
  }, [status]);

  if (!shouldShowStatus) return null;

  return <div className={`online-status ${status}`}>{status}</div>;
}

export default OnlineStatus;
